import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import DeckGL from '@deck.gl/react'
import style from './style.json'
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers'
import { MapView, FlyToInterpolator } from '@deck.gl/core'
import { fitBounds } from '@math.gl/web-mercator'
import { StaticMap } from 'react-map-gl'
import { easeCubic } from 'd3-ease'
import HoverInfo from './HoverInfo'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default

const groupAllLatLng = list => {
  return list.reduce(
    (group, node) => {
      const lat = parseFloat(node.latitud.replace(',', '.'))
      const lng = parseFloat(node.longitud.replace(',', '.'))
      group.lats.push(lat)
      group.lngs.push(lng)

      return group
    },
    { lats: [], lngs: [] },
  )
}

const calculateBoundingBox = nodes => {
  const group = groupAllLatLng(nodes)
  // console.log('los nodes', nodes)

  const minlat = Math.min.apply(null, group.lats)
  const maxlat = Math.max.apply(null, group.lats)
  const minlng = Math.min.apply(null, group.lngs)
  const maxlng = Math.max.apply(null, group.lngs)
  return [
    [minlng, minlat],
    [maxlng, maxlat],
  ]
}

const mapStyleMain =
  'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json'

const MAPBOX_ACCESS_TOKEN =
  'pk.eyJ1Ijoia3l1dW1laXRhaSIsImEiOiJja25pN25qMGUybjR4Mndwamx4dmlybnhqIn0._k3UORwQAorlcfMV-Bp4dw'

const INITIAL_VIEW_STATE = {
  main: {
    longitude: -70.6344719,
    latitude: -33.437529,
    zoom: 13,
    minZoom: 6,
    pitch: 60,
    maxZoom: 14,
  },
}

const Mapa = ({
  nodes,
  acumulados,
  allVisibles,
  triggerFromOutside,
  resetMapView,
  onTriggerMapAll,
}) => {
  const [hoverInfo, setHoverInfo] = useState()
  const [allMarked, setAllMarked] = useState(false)
  useEffect(() => {
    if (triggerFromOutside) {
      // handleTriggerFromOutside(triggerFromOutside)
      console.log('this has triggered from outside', triggerFromOutside)
    }
  }, [triggerFromOutside])

  useEffect(() => {
    if (resetMapView) {
      console.log('resetting resetMapView', resetMapView)
      triggerMapToBounds(nodes)
    }
  }, [resetMapView])

  useEffect(() => {
    if (onTriggerMapAll) {
      console.log('resetting resetMapView all', onTriggerMapAll)
      triggerMapToBounds(allVisibles)
    }
  }, [onTriggerMapAll])

  useEffect(() => {
    // console.log('😊 allVisibles', allVisibles)
    if (allVisibles.length > 0) {
      setAllMarked(true)
      triggerMapToBounds(allVisibles.filter(node => node.triggered))
    } else {
      setAllMarked(false)
    }
  }, [allVisibles])

  // const handleTriggerFromOutside = nodes => {
  //   console.log('zooming from outside:', nodes)
  //   setActiveNodes(nodes)
  //   const boundingbox = calculateBoundingBox(nodes)
  //   const mainRef = { ...mapCallRef.current.viewports[0] }
  //   // console.log('triggering map to bounds', boundingbox)

  //   const { latitude, longitude, zoom } = fitBounds({
  //     width: mainRef.width,
  //     height: mainRef.height * 1.1,
  //     bounds: boundingbox,
  //   })

  //   setViewState(currentViewState => ({
  //     main: {
  //       ...currentViewState.main,
  //       latitude,
  //       longitude,
  //       zoom,
  //       transitionInterpolator: new FlyToInterpolator(),
  //       transitionEasing: easeCubic,
  //       transitionDuration: 'auto',
  //     },
  //   }))
  // }

  // useEffect(() => {
  //   console.log('hoverInfo', hoverInfo)
  // }, hoverInfo)
  const layers = [
    new ScatterplotLayer({
      id: 'all',
      data: allVisibles,
      stroked: false,
      filled: true,
      opacity: 0.8,
      getPosition: d => [
        parseFloat(d.longitud.replace(',', '.')),
        parseFloat(d.latitud.replace(',', '.')),
      ],
      getRadius: 30,
      radiusMinPixels: 8,
      radiusMaxPixels: 20,
      getFillColor: d => (d.triggered ? [255, 20, 0] : [255, 200, 0]),
      pickable: true,
      onClick: info => onSelect(info),
      onHover: info => setHoverInfo(info),
    }),
    new ScatterplotLayer({
      id: 'acumulados',
      data: acumulados,
      stroked: false,
      filled: allMarked ? false : true,
      opacity: 0.3,
      getPosition: d => [
        parseFloat(d.longitud.replace(',', '.')),
        parseFloat(d.latitud.replace(',', '.')),
      ],
      getRadius: 20,
      radiusMinPixels: 8,
      radiusMaxPixels: 20,
      getFillColor: [150, 150, 150],
      pickable: true,
      onClick: info => onSelect(info),
      onHover: info => setHoverInfo(info),
    }),
    new ScatterplotLayer({
      id: 'violencias',
      data: nodes,
      stroked: false,
      filled: allMarked ? false : true,
      opacity: 0.8,
      getPosition: d => [
        parseFloat(d.longitud.replace(',', '.')),
        parseFloat(d.latitud.replace(',', '.')),
      ],
      getRadius: 30,
      radiusMinPixels: 8,
      radiusMaxPixels: 20,
      getFillColor: d => (d.triggered ? [255, 20, 0] : [255, 200, 0]),
      // updateTriggers: {
      //   getFillColor: [activeNodes],
      // },
      pickable: true,
      onClick: info => onSelect(info),
      onHover: info => setHoverInfo(info),
    }),
  ]

  const mainView = new MapView({ id: 'main', controller: true })

  const [highlight, setHighlight] = useState(null)
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE)

  const onViewStateChange = useCallback(({ viewState: newViewState }) => {
    setViewState(() => ({
      main: {
        ...newViewState,
      },
      minimap: {
        ...INITIAL_VIEW_STATE.minimap,
        longitude: newViewState.longitude,
        latitude: newViewState.latitude,
      },
    }))
  }, [])

  const onSelect = useCallback(incidente => {
    if (incidente) {
      setViewState(currentViewState => ({
        main: {
          ...currentViewState.main,
          longitude: parseFloat(incidente.object.longitud.replace(',', '.')),
          latitude: parseFloat(incidente.object.latitud.replace(',', '.')),
          zoom: 12,
          transitionInterpolator: new FlyToInterpolator(),
          transitionDuration: 'auto',
        },
      }))
      setHighlight(incidente)
    }
  }, [])

  const mapCallRef = useRef()

  const triggerMapToBounds = thenodes => {
    // console.log('thenodes', thenodes)
    const activeNodes = thenodes.filter(d => d.triggered)
    // console.log('active thenodes', activeNodes)
    if (activeNodes.length > 0) {
      if (viewState && mapCallRef.current && mapCallRef.current.viewports) {
        const boundingbox = calculateBoundingBox(activeNodes)
        const mainRef = { ...mapCallRef.current.viewports[0] }
        // console.log('triggering map to bounds', boundingbox)

        const { latitude, longitude, zoom } = fitBounds({
          width: mainRef.width * 0.7,
          height: mainRef.height * 0.7,
          bounds: boundingbox,
        })

        // console.log('before setViewState', latitude, longitude, zoom)
        setViewState(currentViewState => ({
          main: {
            ...currentViewState.main,
            latitude,
            longitude,
            zoom,
            transitionInterpolator: new FlyToInterpolator(),
            transitionEasing: easeCubic,
            transitionDuration: 'auto',
          },
        }))
      }
    } else if (thenodes.length > 1) {
      // console.log('thenodes changed', viewState, mapCallRef, thenodes)
      if (viewState && mapCallRef.current && mapCallRef.current.viewports) {
        const boundingbox = calculateBoundingBox(thenodes)
        const mainRef = { ...mapCallRef.current.viewports[0] }
        // console.log('triggering map to bounds', boundingbox)

        const { latitude, longitude, zoom } = fitBounds({
          width: mainRef.width * 0.7,
          height: mainRef.height * 0.7,
          bounds: boundingbox,
        })

        // console.log('before setViewState', latitude, longitude, zoom)
        setViewState(currentViewState => ({
          main: {
            ...currentViewState.main,
            latitude,
            longitude,
            zoom,
            transitionInterpolator: new FlyToInterpolator(),
            transitionEasing: easeCubic,
            transitionDuration: 'auto',
          },
        }))
      }
    } else if (thenodes.length > 0) {
      // console.log('thenodes.length > 0', thenodes)
      setViewState(currentViewState => ({
        main: {
          ...currentViewState.main,
          latitude: thenodes[0].latitud,
          longitude: thenodes[0].longitud,
          zoom: 6,
          transitionInterpolator: new FlyToInterpolator(),
          transitionEasing: easeCubic,
          transitionDuration: 'auto',
        },
      }))
    }
  }

  useEffect(() => {
    triggerMapToBounds(nodes)
  }, [nodes, mapCallRef.current])

  return (
    <DeckGL
      controller={true}
      layers={layers}
      views={mainView}
      viewState={viewState}
      onViewStateChange={onViewStateChange}
      ref={mapCallRef}>
      <MapView id="main" key="main">
        <StaticMap
          mapStyle={style}
          mapboxApiAccessToken={MAPBOX_ACCESS_TOKEN}
        />
      </MapView>

      {hoverInfo && hoverInfo.object && (
        <div
          style={{
            position: 'absolute',
            zIndex: 1,
            pointerEvents: 'none',
            left: hoverInfo.x,
            top: hoverInfo.y,
          }}>
          <HoverInfo node={hoverInfo.object} />
        </div>
      )}

      {highlight && highlight.object && (
        <div
          style={{
            position: 'absolute',
            zIndex: 1,
            pointerEvents: 'none',
            left: highlight.x,
            bottom: highlight.y,
          }}>
          <HoverInfo node={highlight.object} />
        </div>
      )}
    </DeckGL>
  )
}

export default Mapa
